/**
 * Role-name constants — single source of truth.
 *
 * Field (mobile-only) roles vs. office roles are distinguished purely by
 * name today (`AuthRole` only carries `{ name, isAdmin }` — see
 * `client/api/auth.service.ts`). Keeping the field role names in one place
 * means a rename is a one-line change instead of a grep-and-replace across
 * the client.
 */

/** Field worker — warehouse operations only (scan, inventory, receive). */
export const FIELD_WAREHOUSE = 'field_warehouse' as const;
/** Field worker — jobsite operations only (lookup, jobs, lots, areas, photos). */
export const FIELD_JOBSITE = 'field_jobsite' as const;
/** Field worker — both warehouse + jobsite feature grants. */
export const FIELD_ALL = 'field_all' as const;

/**
 * Production DEPARTMENT roles — one per production stage (card aaHMG95zsq83).
 *
 * "department = ROLE": a worker's department IS their RBAC role. There is no
 * separate person↔department table — assigning someone to SAW means granting
 * `production_saw` via user_organization_roles. The stage name embedded in the
 * role gates + scopes what they see in /field/production. Derived from the
 * production sequence in activity_types.autocomplete_order
 * (Template · CAD · Programming · SAW · Lamination).
 */
export const PRODUCTION_TEMPLATE = 'production_template' as const;
export const PRODUCTION_CAD = 'production_cad' as const;
export const PRODUCTION_PROGRAMMING = 'production_programming' as const;
export const PRODUCTION_SAW = 'production_saw' as const;
export const PRODUCTION_LAMINATION = 'production_lamination' as const;

/** The sees-all / can-assign production role (seeded in RBAC migrations). */
export const FIELD_PRODUCTION_MANAGER = 'field_production_manager' as const;

/** Every per-stage production department role. */
export const PRODUCTION_STAGE_ROLES = [
  PRODUCTION_TEMPLATE,
  PRODUCTION_CAD,
  PRODUCTION_PROGRAMMING,
  PRODUCTION_SAW,
  PRODUCTION_LAMINATION,
] as const;

export type ProductionStageRoleName = (typeof PRODUCTION_STAGE_ROLES)[number];

/**
 * Map a production stage role → the activities.department value it completes.
 * `activities.department` is stored upper-cased (SAW, LAMINATION, …); the role
 * name is `production_<stage>` lower-cased. This is the single translation
 * between the two representations.
 */
export function stageForProductionRole(
  roleName: string | null | undefined,
): string | null {
  if (!roleName) {
    return null;
  }
  if (!(PRODUCTION_STAGE_ROLES as readonly string[]).includes(roleName)) {
    return null;
  }
  return roleName.replace('production_', '').toUpperCase();
}

/** Every mobile-only (field) role name. */
export const MOBILE_ONLY_ROLES = [
  FIELD_WAREHOUSE,
  FIELD_JOBSITE,
  FIELD_ALL,
  ...PRODUCTION_STAGE_ROLES,
  FIELD_PRODUCTION_MANAGER,
] as const;

export type MobileOnlyRoleName = (typeof MOBILE_ONLY_ROLES)[number];

/**
 * True when the given role name is one of the mobile-only field roles.
 * Accepts `undefined`/`null` (unauthenticated / not-yet-loaded) and returns
 * false so callers don't need a separate guard.
 *
 * NOTE: This is the legacy NAME-based check. The authoritative signal for
 * "should this user be sequestered to the field app?" is now the per-role
 * `app_access` flag (card BnkVHTRg-Aih) — see `isFieldOnly` below, which is
 * what landing + route-guard decisions use. This name check is retained only
 * as the feature-permission fallback inside `usePagePermission`.
 */
export function isMobileOnlyRole(roleName: string | null | undefined): boolean {
  if (!roleName) {
    return false;
  }
  return (MOBILE_ONLY_ROLES as readonly string[]).includes(roleName);
}

/** Minimal shape of the signed-in role the app-access helpers need. */
export interface RoleAppAccess {
  isAdmin?: boolean;
  appAccess?: 'field_only' | 'both';
}

/**
 * Is the current user restricted to the field/mobile shell?
 *
 * Reads the effective `app_access` the server already resolved as the UNION of
 * ALL the user's roles (card BnkVHTRg-Aih). Admins are never field-only. When
 * `appAccess` is absent (older cached session, unauthenticated, or a role that
 * predates this flag), we default to NOT field-only — i.e. `both` — so nobody
 * is sequestered out of the desktop app by surprise.
 *
 * This is the single source of truth for the post-login shell decision
 * (LoginForm) and the desktop→field redirect guard (routes.tsx).
 */
export function isFieldOnly(role: RoleAppAccess | null | undefined): boolean {
  if (!role) {
    return false;
  }
  if (role.isAdmin) {
    return false;
  }
  return role.appAccess === 'field_only';
}
